const isLocalhost = () => window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname === '10.0.0.1';

type CookieOpts = {
    name: string;
    maxAge?: number; // seconds // omit maxAge for session cookie
    path?: string;
    domain?: string;
};

const DEFAULTS: CookieOpts = {
    name: 'kappla',
    domain: isLocalhost() ? undefined : window.location.hostname.split('.').slice(1).join('.'),
    maxAge: 60 * 60 * 24 * 365,
    path: '/',
};

/**
 * Cookie Security: Secure and SameSite flags
 *
 * - `Secure`: Ensures the cookie is only sent over HTTPS connections, preventing
 *   transmission over unencrypted HTTP where it could be intercepted.
 *   Skipped on localhost for local development convenience.
 *
 * - `SameSite=Lax`: Prevents the cookie from being sent in cross-site requests
 *   (e.g. when a third-party site submits a form to our domain), mitigating
 *   CSRF attacks. `Lax` allows the cookie on top-level navigations (e.g. clicking
 *   a link to our site) but blocks it on cross-origin subrequests.
 *
 * Note: While these cookies store user preferences (not auth tokens), applying
 * these flags is a defense-in-depth measure. The MSAL auth library manages its
 * own token cache in sessionStorage separately.
 */
const persistCookie = (options: CookieOpts, data: Record<string, any>) => {
    const { name = 'opp', maxAge, path, domain } = options;

    let cookie = `${name}=${encodeURIComponent(JSON.stringify(data))}`;

    if (maxAge) {
        cookie += `; max-age=${maxAge}`;
    }

    if (path) {
        cookie += `; path=${path}`;
    }

    if (domain) {
        cookie += `; domain=${domain}`;
    }

    cookie += '; SameSite=Lax';

    if (!isLocalhost()) {
        cookie += '; Secure';
    }

    document.cookie = cookie;
};

const CookieJar = (options: CookieOpts) => {
    let data: Record<string, any> = {};
    const combinedOptions = {
        ...DEFAULTS,
        ...options,
    };

    return {
        set: (key: string, value: any) => {
            data[key] = value;
            persistCookie(combinedOptions, data);
        },

        get: (key: string, defaultValue?: any) => data[key] ?? defaultValue,

        removeCookie: (key: string) => {
            document.cookie = `${combinedOptions.name}=; max-age=-1`;
            data = {};
            if (key in data) {
                delete data[key];
                persistCookie(combinedOptions, data);
            }
        },

        removeAll: () => {
            document.cookie = `${combinedOptions.name}=; max-age=-1`;
            data = {};
            persistCookie(combinedOptions, data);
        },
    };
};

export default CookieJar;
